import { useState, useRef, useEffect, useCallback } from 'react';
import { safeJson, networkMessage } from '../apiHelpers';
import { t } from '../i18n';

const API_BASE = '/api/family';

function getAuthToken() {
  return localStorage.getItem('token') || '';
}

function authHeaders() {
  const token = getAuthToken();
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Token ${token}` } : {}) };
}

const colors = {
  bg: '#F7F5F0', surface: '#fff', border: '#E3E1D9', text: '#1F1E1B',
  textMuted: '#5F5E5A', primary: '#0F6E56', primaryBg: '#E1F5EE', danger: '#A32D2D',
};

function FamilyHelpModal({ onClose }) {
  const sectionStyle = { marginBottom: 20 };
  const headingStyle = { fontSize: 16, fontWeight: 600, margin: '0 0 8px' };
  const textStyle = { fontSize: 14, color: '#3A3935', lineHeight: 1.6, margin: '0 0 8px' };
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px 12px', overflowY: 'auto' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, maxWidth: 640, width: '100%', padding: '1.5rem', marginTop: 20 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>{t('fth_title')}</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: colors.textMuted }}>✕</button>
        </div>
        <div style={sectionStyle}>
          <p style={headingStyle}>{t('flh_what_q')}</p>
          <p style={textStyle}>{t('fth_what')}</p>
        </div>
        <div style={sectionStyle}>
          <p style={headingStyle}>{t('fth_start_q')}</p>
          <p style={textStyle}>{t('fth_s1')}</p>
          <p style={textStyle}>{t('fth_s2')}</p>
          <p style={textStyle}>{t('fth_s3')}</p>
          <p style={textStyle}>{t('fth_s4')}</p>
        </div>
        <div style={sectionStyle}>
          <p style={headingStyle}>{t('fth_prem_q')}</p>
          <p style={textStyle}>{t('fth_prem')}</p>
        </div>
        <button onClick={onClose} style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('got_it')}</button>
      </div>
    </div>
  );
}

function PersonCard({ person, onEdit, isOwner, todayBirthday }) {
  const [showCode, setShowCode] = useState(false);

  const sendWish = async (e) => {
    e.stopPropagation();
    const message = prompt(`Send a birthday wish to ${person.full_name}:`, 'Happy Birthday! 🎉');
    if (!message) return;
    try {
      await fetch(`${API_BASE}/people/${person.id}/wish/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ message }) });
      alert(t('ft_wish'));
    } catch (e2) { alert('Could not send wish.'); }
  };

  return (
    <div style={{ background: colors.surface, border: todayBirthday ? `2px solid #F2C879` : `0.5px solid ${colors.border}`, borderRadius: 10, padding: '0.75rem', textAlign: 'center', position: 'relative' }}>
      {todayBirthday && <span style={{ position: 'absolute', top: -10, right: -6, fontSize: 20 }}>🎂</span>}
      <div onClick={() => onEdit(person)} style={{ cursor: 'pointer' }}>
        <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#F1EFE8', margin: '0 auto 8px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {person.photo ? <img src={person.photo} alt={person.full_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 24 }}>{person.gender === 'female' ? '👩' : person.gender === 'male' ? '👨' : '🧑'}</span>}
        </div>
        <p style={{ fontSize: 13, fontWeight: 500, margin: 0 }}>{person.full_name}</p>
        <p style={{ fontSize: 11, color: colors.textMuted, margin: 0 }}>
          {person.date_of_birth ? new Date(person.date_of_birth).getFullYear() : '?'}
          {!person.is_alive && person.date_of_death ? ` – ${new Date(person.date_of_death).getFullYear()}` : person.is_alive ? '' : ' – †'}
        </p>
        {person.is_claimed && <span style={{ fontSize: 10, color: colors.primary }}>✓ Claimed by @{person.claimed_by_username}</span>}
      </div>
      {todayBirthday && (
        <button onClick={sendWish} style={{ marginTop: 6, fontSize: 11, padding: '3px 10px', border: 'none', borderRadius: 10, background: '#FFF3C4', color: '#8A6D00', cursor: 'pointer' }}>{t('ft_wish')}</button>
      )}
      {isOwner && !person.is_claimed && (
        <button onClick={(e) => { e.stopPropagation(); setShowCode(!showCode); }} style={{ marginTop: 6, fontSize: 10, padding: '3px 8px', border: `0.5px solid ${colors.border}`, borderRadius: 10, background: '#fff', cursor: 'pointer', display: 'block', width: '100%' }}>
          {showCode ? 'Hide code' : '🔗 Invite code'}
        </button>
      )}
      {showCode && (
        <div style={{ marginTop: 6 }}>
          <img src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(`${window.location.origin}/family-tree?claim=${person.claim_code}`)}`} alt="QR" style={{ width: 80, height: 80, margin: '0 auto' }} />
          <p style={{ fontSize: 10, fontFamily: 'monospace', color: colors.textMuted, margin: '4px 0 0' }}>{person.claim_code}</p>
        </div>
      )}
    </div>
  );
}

function PersonForm({ tree, people, editingPerson, onSaved, onCancel }) {
  const [form, setForm] = useState(editingPerson || { first_name: '', last_name: '', gender: 'male', date_of_birth: '', date_of_death: '', birthplace: '', bio: '', phone: '', father_id: '', mother_id: '' });
  const [photoFile, setPhotoFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 6, boxSizing: 'border-box' };
  const labelStyle = { fontSize: 13, color: colors.textMuted, display: 'block', marginBottom: 4 };

  const submit = async () => {
    setSubmitting(true);
    try {
      let personId = editingPerson?.id;
      if (editingPerson) {
        await fetch(`${API_BASE}/people/${personId}/update/`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(form) });
      } else {
        const res = await fetch(`${API_BASE}/trees/${tree.id}/people/create/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(form) });
        const data = await safeJson(res);
        if (!res.ok) throw new Error(data.error || 'Could not add person.');
        personId = data.id;
      }
      if (photoFile) {
        const fd = new FormData();
        fd.append('photo', photoFile);
        const token = getAuthToken();
        await fetch(`${API_BASE}/people/${personId}/upload-photo/`, { method: 'POST', headers: token ? { Authorization: `Token ${token}` } : {}, body: fd });
      }
      onSaved();
    } catch (e) {
      alert(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const deletePerson = async () => {
    if (!window.confirm('Remove this person from the tree?')) return;
    await fetch(`${API_BASE}/people/${editingPerson.id}/delete/`, { method: 'DELETE', headers: authHeaders() });
    onSaved();
  };

  return (
    <div style={{ background: '#F1EFE8', border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1.25rem', marginBottom: '1rem' }}>
      <p style={{ fontWeight: 500, fontSize: 15, margin: '0 0 12px' }}>{editingPerson ? 'Edit person' : 'Add a person'}</p>
      <div style={{ display: 'grid', gap: 10 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>{t('first_name')}</label><input style={inputStyle} value={form.first_name} onChange={update('first_name')} /></div>
          <div><label style={labelStyle}>{t('last_name')}</label><input style={inputStyle} value={form.last_name} onChange={update('last_name')} /></div>
        </div>
        <div><label style={labelStyle}>{t('gender')}</label>
          <select style={inputStyle} value={form.gender} onChange={update('gender')}>
            <option value="male">{t('male')}</option><option value="female">{t('female')}</option><option value="other">{t('other_gender')}</option>
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>{t('date_of_birth')}</label><input type="date" style={inputStyle} value={form.date_of_birth || ''} onChange={update('date_of_birth')} /></div>
          <div><label style={labelStyle}>{t('ft_dod')}</label><input type="date" style={inputStyle} value={form.date_of_death || ''} onChange={update('date_of_death')} /></div>
        </div>
        <div><label style={labelStyle}>{t('birthplace')}</label><input style={inputStyle} value={form.birthplace} onChange={update('birthplace')} /></div>
        <div><label style={labelStyle}>{t('ft_phone')}</label><input style={inputStyle} value={form.phone} onChange={update('phone')} /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>{t('father_label')}</label>
            <select style={inputStyle} value={form.father_id || ''} onChange={update('father_id')}>
              <option value="">— {t('ft_none')} —</option>
              {people.filter((p) => p.id !== editingPerson?.id).map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
            </select>
          </div>
          <div><label style={labelStyle}>{t('mother_label')}</label>
            <select style={inputStyle} value={form.mother_id || ''} onChange={update('mother_id')}>
              <option value="">— {t('ft_none')} —</option>
              {people.filter((p) => p.id !== editingPerson?.id).map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
            </select>
          </div>
        </div>
        <div><label style={labelStyle}>{t('ft_bio')}</label><textarea style={{ ...inputStyle, resize: 'vertical' }} rows={2} value={form.bio} onChange={update('bio')} /></div>
        <div><label style={labelStyle}>{t('photo_label')}</label><input type="file" accept="image/*" onChange={(e) => setPhotoFile(e.target.files[0])} /></div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button onClick={submit} disabled={submitting || !form.first_name} style={{ flex: 1, padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
          {submitting ? 'Saving...' : 'Save'}
        </button>
        <button onClick={onCancel} style={{ padding: '10px 16px', fontSize: 14, border: `0.5px solid ${colors.border}`, borderRadius: 6, background: '#fff', cursor: 'pointer' }}>{t('cancel')}</button>
        {editingPerson && <button onClick={deletePerson} style={{ padding: '10px 16px', fontSize: 14, border: 'none', borderRadius: 6, background: '#FBEAE3', color: colors.danger, cursor: 'pointer' }}>{t('delete_btn')}</button>}
      </div>
    </div>
  );
}

function FamilyPhotoWall({ tree }) {
  const structure = buildTreeStructure(tree.people, tree.marriages);

  const gen = {};
  function getGen(id, seen) {
    if (gen[id] !== undefined) return gen[id];
    if (seen.has(id)) return 0;
    seen.add(id);
    const p = structure.byId[id];
    if (!p) return 0;
    const parentIds = [p.father_id, p.mother_id].filter((x) => x && structure.byId[x]);
    if (parentIds.length === 0) {
      gen[id] = 0;
    } else {
      gen[id] = Math.max(...parentIds.map((pid) => getGen(pid, seen) + 1));
    }
    return gen[id];
  }
  tree.people.forEach((p) => getGen(p.id, new Set()));

  const withPhotos = tree.people.filter((p) => p.photo);
  const maxGen = Math.max(0, ...withPhotos.map((p) => gen[p.id] || 0));
  const rows = [];
  for (let g = 0; g <= maxGen; g++) {
    const peopleInGen = withPhotos.filter((p) => gen[p.id] === g);
    if (peopleInGen.length) rows.push({ gen: g, people: peopleInGen });
  }

  if (withPhotos.length === 0) {
    return <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('no_photos_added')}</p>;
  }

  return (
    <div>
      {rows.map(({ gen: g, people }) => (
        <div key={g} style={{ marginBottom: 22 }}>
          <p style={{ fontSize: 11, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
            {g === 0 ? 'Eldest generation' : `Generation ${g + 1}`}
          </p>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {people.map((p) => (
              <div key={p.id} style={{ textAlign: 'center', width: 90 }}>
                <img src={p.photo} alt={p.full_name} style={{ width: 80, height: 80, borderRadius: 10, objectFit: 'cover', marginBottom: 4 }} />
                <p style={{ fontSize: 11, fontWeight: 500, margin: 0 }}>{p.full_name}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function AlbumPanel({ tree }) {
  const [photos, setPhotos] = useState([]);
  const [caption, setCaption] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [albumView, setAlbumView] = useState('wall');

  const load = useCallback(async () => {
    const res = await fetch(`${API_BASE}/trees/${tree.id}/album/`, { headers: getAuthToken() ? authHeaders() : {} });
    if (res.ok) setPhotos((await safeJson(res)).photos);
  }, [tree.id]);

  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('caption', caption);
      const token = getAuthToken();
      const res = await fetch(`${API_BASE}/trees/${tree.id}/album/upload/`, { method: 'POST', headers: token ? { Authorization: `Token ${token}` } : {}, body: fd });
      if (!res.ok) throw new Error('Could not upload.');
      setCaption(''); setFile(null);
      load();
    } catch (e) { alert(networkMessage(e)); } finally { setUploading(false); }
  };

  const deletePhoto = async (id) => {
    if (!window.confirm('Delete this photo?')) return;
    await fetch(`${API_BASE}/album/${id}/delete/`, { method: 'DELETE', headers: authHeaders() });
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        <button onClick={() => setAlbumView('wall')} style={{ padding: '4px 12px', fontSize: 12, borderRadius: 14, border: `0.5px solid ${colors.border}`, background: albumView === 'wall' ? colors.primaryBg : '#fff', color: albumView === 'wall' ? colors.primary : colors.textMuted, cursor: 'pointer' }}>{t('ft_photo_wall')}</button>
        <button onClick={() => setAlbumView('shared')} style={{ padding: '4px 12px', fontSize: 12, borderRadius: 14, border: `0.5px solid ${colors.border}`, background: albumView === 'shared' ? colors.primaryBg : '#fff', color: albumView === 'shared' ? colors.primary : colors.textMuted, cursor: 'pointer' }}>{t('ft_album')}</button>
      </div>

      {albumView === 'wall' ? (
        <FamilyPhotoWall tree={tree} />
      ) : (
        <>
          {tree.is_owner && (
            <div style={{ background: '#F1EFE8', borderRadius: 10, padding: '0.75rem', marginBottom: 12 }}>
              <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} style={{ marginBottom: 8 }} />
              <input placeholder={t('caption_optional')} value={caption} onChange={(e) => setCaption(e.target.value)} style={{ width: '100%', padding: '8px', fontSize: 13, border: `0.5px solid ${colors.border}`, borderRadius: 6, boxSizing: 'border-box', marginBottom: 8 }} />
              <button onClick={upload} disabled={uploading || !file} style={{ width: '100%', padding: '8px', fontSize: 13, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
                {uploading ? 'Uploading...' : '+ Add to album'}
              </button>
            </div>
          )}
          {photos.length === 0 ? <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('no_photos_in_album')}</p> : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10 }}>
              {photos.map((p) => (
                <div key={p.id} style={{ position: 'relative' }}>
                  <img src={p.image} alt={p.caption} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 8 }} />
                  {p.caption && <p style={{ fontSize: 11, color: colors.textMuted, margin: '4px 0 0' }}>{p.caption}</p>}
                  {tree.is_owner && <button onClick={() => deletePhoto(p.id)} style={{ position: 'absolute', top: 4, right: 4, fontSize: 11, background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer' }}>✕</button>}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PhoneBookPanel({ tree }) {
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/trees/${tree.id}/phonebook/`, { headers: authHeaders() }).then((r) => r.json()).then((data) => setEntries(data.entries || [])).catch(() => setEntries([]));
  }, [tree.id]);

  if (entries === null) return <p style={{ color: colors.textMuted, textAlign: 'center' }}>{t('loading')}</p>;
  if (entries.length === 0) return <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('no_phone_numbers')}</p>;

  return (
    <div>
      {entries.map((e) => (
        <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: '0.6rem 0.75rem', marginBottom: 6 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#F1EFE8', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {e.photo ? <img src={e.photo} alt={e.full_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '🧑'}
          </div>
          <div>
            <p style={{ fontSize: 13, fontWeight: 500, margin: 0 }}>{e.full_name}</p>
            <p style={{ fontSize: 12, color: colors.textMuted, margin: 0 }}>{e.phone}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function SpouseLinkForm({ tree, onLinked }) {
  const [person1, setPerson1] = useState('');
  const [person2, setPerson2] = useState('');
  const [marriageDate, setMarriageDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!person1 || !person2 || person1 === person2) { alert('Pick two different people.'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/trees/${tree.id}/marriages/create/`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ person1_id: person1, person2_id: person2, marriage_date: marriageDate || null }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not link spouses.');
      setPerson1(''); setPerson2(''); setMarriageDate('');
      onLinked();
    } catch (e) { alert(networkMessage(e)); } finally { setSubmitting(false); }
  };

  const selectStyle = { padding: '8px 10px', fontSize: 13, border: '0.5px solid #B4B2A9', borderRadius: 6, flex: 1 };

  return (
    <div style={{ background: '#F1EFE8', borderRadius: 10, padding: '0.75rem', marginBottom: 12 }}>
      <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 8px' }}>{t('ft_marriage')}</p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <select style={selectStyle} value={person1} onChange={(e) => setPerson1(e.target.value)}>
          <option value="">— {t('ft_p1')} —</option>
          {tree.people.map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
        </select>
        <select style={selectStyle} value={person2} onChange={(e) => setPerson2(e.target.value)}>
          <option value="">— {t('ft_p2')} —</option>
          {tree.people.map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
        </select>
      </div>
      <input type="date" value={marriageDate} onChange={(e) => setMarriageDate(e.target.value)} style={{ ...selectStyle, width: '100%', boxSizing: 'border-box', marginBottom: 8 }} />
      <button onClick={submit} disabled={submitting} style={{ width: '100%', padding: '8px', fontSize: 13, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
        {submitting ? 'Linking...' : 'Link as married'}
      </button>
    </div>
  );
}

function buildTreeStructure(people, marriages) {
  const byId = {};
  people.forEach((p) => { byId[p.id] = p; });

  const childrenOf = {};
  people.forEach((p) => {
    [p.father_id, p.mother_id].forEach((pid) => {
      if (pid && byId[pid]) {
        if (!childrenOf[pid]) childrenOf[pid] = [];
        if (!childrenOf[pid].some((c) => c.id === p.id)) childrenOf[pid].push(p);
      }
    });
  });

  const spouseOf = {};
  marriages.forEach((m) => {
    if (byId[m.person1_id] && byId[m.person2_id]) {
      spouseOf[m.person1_id] = m.person2_id;
      spouseOf[m.person2_id] = m.person1_id;
    }
  });

  // Infer couples from any shared child (father+mother of the same kid), even
  // without an explicit marriage link - prevents the same family branch from
  // being rendered twice under two unlinked parents.
  people.forEach((p) => {
    if (p.father_id && p.mother_id && byId[p.father_id] && byId[p.mother_id]) {
      if (!spouseOf[p.father_id]) spouseOf[p.father_id] = p.mother_id;
      if (!spouseOf[p.mother_id]) spouseOf[p.mother_id] = p.father_id;
    }
  });

  const hasParentInTree = new Set();
  people.forEach((p) => {
    if (p.father_id && byId[p.father_id]) hasParentInTree.add(p.id);
    if (p.mother_id && byId[p.mother_id]) hasParentInTree.add(p.id);
  });

  // A person who has no parent in the tree is a root candidate - UNLESS they
  // married into someone who does have a parent in tree (they'll show up as
  // that person's spouse instead, not as their own separate root).
  let roots = people.filter((p) => !hasParentInTree.has(p.id));
  roots = roots.filter((p) => {
    const sid = spouseOf[p.id];
    return !(sid && hasParentInTree.has(sid));
  });

  const rootIds = new Set(roots.map((r) => r.id));
  const usedAsSpouse = new Set();
  roots = roots.filter((r) => {
    const sid = spouseOf[r.id];
    if (sid && rootIds.has(sid)) {
      if (usedAsSpouse.has(r.id)) return false;
      usedAsSpouse.add(sid);
    }
    return true;
  });

  return { byId, childrenOf, spouseOf, roots };
}

function TreeNode({ person, structure, onEdit, onFocus, todayBirthdayIds, renderedIds }) {
  renderedIds.add(person.id);
  const spouseId = structure.spouseOf[person.id];
  const spouse = spouseId ? structure.byId[spouseId] : null;
  if (spouse) renderedIds.add(spouse.id);

  const childIds = new Set();
  (structure.childrenOf[person.id] || []).forEach((c) => childIds.add(c.id));
  if (spouse) (structure.childrenOf[spouse.id] || []).forEach((c) => childIds.add(c.id));
  const children = [...childIds].map((id) => structure.byId[id]).filter((c) => c && !renderedIds.has(c.id));

  const miniCard = (p) => (
    <div style={{ textAlign: 'center', width: 80 }}>
      <div onClick={() => onEdit(p)} style={{ cursor: 'pointer', width: 52, height: 52, borderRadius: '50%', background: '#F1EFE8', margin: '0 auto 4px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', border: todayBirthdayIds.includes(p.id) ? '2px solid #F2C879' : `1px solid ${colors.border}` }}>
        {p.photo ? <img src={p.photo} alt={p.full_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 20 }}>{p.gender === 'female' ? '👩' : p.gender === 'male' ? '👨' : '🧑'}</span>}
      </div>
      <p style={{ fontSize: 11, fontWeight: 500, margin: 0, lineHeight: 1.2 }}>{p.full_name}{todayBirthdayIds.includes(p.id) && ' 🎂'}</p>
      <p style={{ fontSize: 9, color: colors.textMuted, margin: '0 0 2px' }}>
        {p.date_of_birth ? new Date(p.date_of_birth).getFullYear() : '?'}{!p.is_alive ? ' – †' : ''}
      </p>
      {(structure.childrenOf[p.id] || []).length > 0 && (
        <button onClick={() => onFocus(p.id)} style={{ fontSize: 9, padding: '1px 6px', border: `0.5px solid ${colors.border}`, borderRadius: 8, background: '#fff', cursor: 'pointer' }}>
          🔍 branch
        </button>
      )}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {miniCard(person)}
        {spouse && (
          <>
            <span style={{ fontSize: 14 }}>💍</span>
            {miniCard(spouse)}
          </>
        )}
      </div>
      {children.length > 0 && (
        <>
          <div style={{ width: 2, height: 16, background: colors.border }} />
          <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
            {children.map((child) => (
              <div key={child.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ width: 2, height: 12, background: colors.border }} />
                <TreeNode person={child} structure={structure} onEdit={onEdit} onFocus={onFocus} todayBirthdayIds={todayBirthdayIds} renderedIds={renderedIds} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function findAncestorRoot(personId, structure) {
  function isDescendant(rootId, targetId, visited) {
    if (rootId === targetId) return true;
    if (visited.has(rootId)) return false;
    visited.add(rootId);
    const kids = structure.childrenOf[rootId] || [];
    return kids.some((k) => isDescendant(k.id, targetId, visited));
  }
  return structure.roots.find((r) => isDescendant(r.id, personId, new Set())) || structure.byId[personId];
}

function HierarchyView({ tree, onEdit, todayBirthdayIds }) {
  const [focusId, setFocusId] = useState(null);
  const structure = buildTreeStructure(tree.people, tree.marriages);

  const focusRoot = focusId ? findAncestorRoot(focusId, structure) : null;
  const rootsToShow = focusRoot ? [focusRoot] : structure.roots;

  return (
    <div style={{ overflowX: 'auto' }}>
      {focusId && (
        <button onClick={() => setFocusId(null)} style={{ marginBottom: 12, fontSize: 12, color: colors.primary, background: 'none', border: 'none', cursor: 'pointer' }}>
          ← Show whole tree
        </button>
      )}
      {focusId && focusRoot && (
        <p style={{ fontSize: 12, color: colors.textMuted, marginBottom: 8 }}>
          Showing the whole branch descending from {focusRoot.full_name}{structure.spouseOf[focusRoot.id] && structure.byId[structure.spouseOf[focusRoot.id]] ? ` & ${structure.byId[structure.spouseOf[focusRoot.id]].full_name}` : ''} - includes siblings, cousins, and their families.
        </p>
      )}
      <div style={{ display: 'flex', gap: 40, alignItems: 'flex-start', paddingBottom: 12 }}>
        {rootsToShow.length === 0 ? (
          <p style={{ color: colors.textMuted }}>{t('no_one_to_show')}</p>
        ) : (
          (() => {
            const renderedIds = new Set();
            return rootsToShow.map((root) => (
              <TreeNode key={root.id} person={root} structure={structure} onEdit={onEdit} onFocus={setFocusId} todayBirthdayIds={todayBirthdayIds} renderedIds={renderedIds} />
            ));
          })()
        )}
      </div>
    </div>
  );
}

function TreeView({ treeId, onBack }) {
  const [tree, setTree] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [boosting, setBoosting] = useState(false);
  const [subtab, setSubtab] = useState('people');
  const [viewMode, setViewMode] = useState('hierarchy');
  const [todayBirthdayIds, setTodayBirthdayIds] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/trees/${treeId}/`, { headers: getAuthToken() ? authHeaders() : {} });
      if (res.ok) setTree(await safeJson(res));
    } catch (e) {}
  }, [treeId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetch(`${API_BASE}/birthdays/today/`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((data) => setTodayBirthdayIds((data.birthdays || []).map((b) => b.person_id)))
      .catch(() => {});
  }, []);

  const goPremium = async () => {
    if (!window.Pi) { alert(t('open_pi_pay2')); return; }
    setBoosting(true);
    try {
      await window.Pi.authenticate(['username', 'payments'], () => {});
      window.Pi.createPayment(
        { amount: 5, memo: `Family Tree Premium: ${tree.name}`, metadata: { tree_id: tree.id } },
        {
          onReadyForServerApproval: (paymentId) => fetch(`${API_BASE}/trees/${tree.id}/premium/approve/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId }) }),
          onReadyForServerCompletion: (paymentId, txid) => {
            fetch(`${API_BASE}/trees/${tree.id}/premium/complete/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId, txid, amount: 5, days: 365 }) })
              .then(() => { alert('Premium activated for 1 year!'); setBoosting(false); load(); })
              .catch(() => { alert('Payment reached Pi but activation failed.'); setBoosting(false); });
          },
          onCancel: () => setBoosting(false),
          onError: () => setBoosting(false),
        }
      );
    } catch (e) { setBoosting(false); }
  };

  if (!tree) return <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('loading')}</p>;

  return (
    <div>
      <button onClick={onBack} style={{ fontSize: 13, color: colors.primary, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 12 }}>{t('ft_back_trees')}</button>
      <FamilyRelations tree={tree} />
      <h2 style={{ fontSize: 20, margin: '0 0 4px' }}>{tree.name}</h2>
      <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 12px' }}>
        {tree.people_count}/{tree.is_premium ? '∞' : tree.person_limit} people {tree.is_public && '· 🌐 Public'}
      </p>

      {tree.is_owner && !tree.is_premium && (
        <button onClick={goPremium} disabled={boosting} style={{ marginBottom: 12, padding: '8px 16px', fontSize: 13, fontWeight: 500, border: 'none', borderRadius: 6, background: '#FFF3C4', color: '#8A6D00', cursor: 'pointer' }}>
          {boosting ? 'Processing...' : '⭐ Go Premium (5 Pi / year, unlimited people)'}
        </button>
      )}

      <div style={{ display: 'flex', gap: 4, overflowX: 'auto', borderBottom: `0.5px solid ${colors.border}`, marginBottom: 12 }}>
        <button onClick={() => setSubtab('people')} style={{ padding: '6px 12px', fontSize: 13, fontWeight: 500, border: 'none', borderBottom: subtab === 'people' ? `2px solid ${colors.primary}` : '2px solid transparent', background: 'transparent', color: subtab === 'people' ? colors.primary : colors.textMuted, cursor: 'pointer' }}>{t('people_tab')}</button>
        <button onClick={() => setSubtab('album')} style={{ padding: '6px 12px', fontSize: 13, fontWeight: 500, border: 'none', borderBottom: subtab === 'album' ? `2px solid ${colors.primary}` : '2px solid transparent', background: 'transparent', color: subtab === 'album' ? colors.primary : colors.textMuted, cursor: 'pointer' }}>{t('album_tab')}</button>
        <button onClick={() => setSubtab('phonebook')} style={{ padding: '6px 12px', fontSize: 13, fontWeight: 500, border: 'none', borderBottom: subtab === 'phonebook' ? `2px solid ${colors.primary}` : '2px solid transparent', background: 'transparent', color: subtab === 'phonebook' ? colors.primary : colors.textMuted, cursor: 'pointer' }}>{t('phonebook_tab')}</button>
        <button onClick={() => setSubtab('ftalbum')} style={{ padding: '6px 12px', fontSize: 13, fontWeight: 500, border: 'none', borderBottom: subtab === 'ftalbum' ? `2px solid ${colors.primary}` : '2px solid transparent', background: 'transparent', color: subtab === 'ftalbum' ? colors.primary : colors.textMuted, cursor: 'pointer', whiteSpace: 'nowrap' }}>{t('ftx_album')}</button>
        <button onClick={() => setSubtab('ftdir')} style={{ padding: '6px 12px', fontSize: 13, fontWeight: 500, border: 'none', borderBottom: subtab === 'ftdir' ? `2px solid ${colors.primary}` : '2px solid transparent', background: 'transparent', color: subtab === 'ftdir' ? colors.primary : colors.textMuted, cursor: 'pointer', whiteSpace: 'nowrap' }}>{t('ftx_dir')}</button>
        <button onClick={() => setSubtab('ftevents')} style={{ padding: '6px 12px', fontSize: 13, fontWeight: 500, border: 'none', borderBottom: subtab === 'ftevents' ? `2px solid ${colors.primary}` : '2px solid transparent', background: 'transparent', color: subtab === 'ftevents' ? colors.primary : colors.textMuted, cursor: 'pointer', whiteSpace: 'nowrap' }}>{t('ftx_events')}</button>
      </div>

      {subtab === 'people' && (
        <>
          {tree.is_owner && (
            <button onClick={() => { setEditingPerson(null); setShowForm(true); }} style={{ marginBottom: 12, padding: '8px 16px', fontSize: 13, fontWeight: 500, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
              + Add person
            </button>
          )}

          {showForm && (
            <PersonForm
              tree={tree} people={tree.people} editingPerson={editingPerson}
              onSaved={() => { setShowForm(false); setEditingPerson(null); load(); }}
              onCancel={() => { setShowForm(false); setEditingPerson(null); }}
            />
          )}

          {tree.is_owner && tree.people.length >= 2 && (
            <SpouseLinkForm tree={tree} onLinked={load} />
          )}

          {tree.people.length === 0 ? (
            <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('no_people_added')}</p>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                <button onClick={() => setViewMode('hierarchy')} style={{ padding: '4px 12px', fontSize: 12, borderRadius: 14, border: `0.5px solid ${colors.border}`, background: viewMode === 'hierarchy' ? colors.primaryBg : '#fff', color: viewMode === 'hierarchy' ? colors.primary : colors.textMuted, cursor: 'pointer' }}>{t('ft_tree_view')}</button>
                <button onClick={() => setViewMode('grid')} style={{ padding: '4px 12px', fontSize: 12, borderRadius: 14, border: `0.5px solid ${colors.border}`, background: viewMode === 'grid' ? colors.primaryBg : '#fff', color: viewMode === 'grid' ? colors.primary : colors.textMuted, cursor: 'pointer' }}>{t('ft_grid_view')}</button>
              </div>

              {viewMode === 'hierarchy' ? (
                <HierarchyView
                  tree={tree} todayBirthdayIds={todayBirthdayIds}
                  onEdit={(person) => { if (tree.is_owner) { setEditingPerson(person); setShowForm(true); } }}
                />
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 12 }}>
                  {tree.people.map((p) => (
                    <PersonCard
                      key={p.id} person={p} isOwner={tree.is_owner}
                      todayBirthday={todayBirthdayIds.includes(p.id)}
                      onEdit={(person) => { if (tree.is_owner) { setEditingPerson(person); setShowForm(true); } }}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {subtab === 'album' && <AlbumPanel tree={tree} />}
      {subtab === 'phonebook' && <PhoneBookPanel tree={tree} />}
      {subtab === 'ftalbum' && <ShajraAlbum tree={tree} />}
      {subtab === 'ftdir' && <FamilyDirectory tree={tree} />}
      {subtab === 'ftevents' && <FamilyEventsPanel tree={tree} />}
    </div>
  );
}

export default function FamilyTreeApp() {
  const [trees, setTrees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTreeName, setNewTreeName] = useState('');
  const [claimCode, setClaimCode] = useState('');
  const [openTreeId, setOpenTreeId] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/trees/mine/`, { headers: authHeaders() });
      if (res.ok) setTrees((await safeJson(res)).trees);
    } catch (e) {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createTree = async () => {
    if (!newTreeName) return;
    try {
      const res = await fetch(`${API_BASE}/trees/create/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ name: newTreeName }) });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not create tree.');
      setNewTreeName('');
      load();
      setOpenTreeId(data.slug);
    } catch (e) { alert(networkMessage(e)); }
  };

  const claimIdentity = async () => {
    if (!claimCode) return;
    try {
      const res = await fetch(`${API_BASE}/claim/${claimCode.toUpperCase()}/confirm/`, { method: 'POST', headers: authHeaders() });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not claim.');
      alert(`You've claimed the identity of ${data.person_name}!`);
      setClaimCode('');
    } catch (e) { alert(networkMessage(e)); }
  };

  if (openTreeId) {
    return (
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem', fontFamily: 'sans-serif', background: colors.bg, minHeight: '100vh' }}>
        <TreeView treeId={openTreeId} onBack={() => { setOpenTreeId(null); load(); }} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem', fontFamily: 'sans-serif', background: colors.bg, minHeight: '100vh' }}>
      {showHelp && <FamilyHelpModal onClose={() => setShowHelp(false)} />}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontSize: 22, margin: '0 0 4px' }}>{t('ft_app')}</h1>
          <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 1rem' }}>{t('family_tree_tagline')}</p>
        </div>
        <button onClick={() => setShowHelp(true)} style={{ padding: '6px 14px', fontSize: 13, fontWeight: 500, border: `0.5px solid ${colors.border}`, borderRadius: 20, background: '#fff', cursor: 'pointer' }}>{t('how_it_works_btn')}</button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: '1rem' }}>
        <input placeholder={t('new_tree_name')} value={newTreeName} onChange={(e) => setNewTreeName(e.target.value)} style={{ flex: 1, padding: '10px 14px', fontSize: 14, border: `0.5px solid #B4B2A9`, borderRadius: 20, boxSizing: 'border-box' }} />
        <button onClick={createTree} style={{ padding: '0 20px', fontSize: 13, fontWeight: 500, border: 'none', borderRadius: 20, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('create_btn')}</button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: '1.5rem', background: '#F1EFE8', padding: '0.6rem', borderRadius: 12 }}>
        <input placeholder={t('invite_code_placeholder')} value={claimCode} onChange={(e) => setClaimCode(e.target.value.toUpperCase())} style={{ flex: 1, padding: '8px 12px', fontSize: 13, border: `0.5px solid ${colors.border}`, borderRadius: 16, boxSizing: 'border-box' }} />
        <button onClick={claimIdentity} style={{ padding: '0 16px', fontSize: 12, fontWeight: 500, border: 'none', borderRadius: 16, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('claim_btn')}</button>
      </div>

      {loading ? <p style={{ color: colors.textMuted, textAlign: 'center' }}>{t('loading')}</p> :
        trees.length === 0 ? <p style={{ color: colors.textMuted, textAlign: 'center' }}>{t('no_trees_yet')}</p> :
        trees.map((t) => (
          <div key={t.id} onClick={() => setOpenTreeId(t.slug)} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: '0.75rem', marginBottom: 8, cursor: 'pointer' }}>
            <p style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>{t.name} {t.is_premium && '⭐'}</p>
            <p style={{ fontSize: 12, color: colors.textMuted, margin: 0 }}>{t.people_count}/{t.is_premium ? '∞' : t.person_limit} people {t.is_public && '· 🌐 Public'}</p>
          </div>
        ))
      }
    </div>
  );
}


// ================= Rishtay engine (Family Tree relations) =================
const FT_REL_API = '/api/family';
const ftAuthH = () => {
  const h = { 'Content-Type': 'application/json' };
  const tk = localStorage.getItem('token');
  if (tk) h['Authorization'] = `Token ${tk}`;
  return h;
};

function ftName(p) {
  return p.full_name || p.name || [p.first_name, p.last_name].filter(Boolean).join(' ') || ('#' + p.id);
}

function computeRelations(people, marriages, meId, genders) {
  const R = {};
  if (!meId) return R;
  const by = {}; people.forEach((p) => { by[p.id] = p; });
  const fa = (id) => { const p = by[id]; return p ? (p.father_id ?? p.father ?? null) : null; };
  const mo = (id) => { const p = by[id]; return p ? (p.mother_id ?? p.mother ?? null) : null; };
  const g = (id) => (genders && genders[id]) || '';
  const dob = (id) => (by[id] && (by[id].dob || by[id].date_of_birth)) || '';
  const kids = (id) => people.filter((p) => (p.father_id ?? p.father) === id || (p.mother_id ?? p.mother) === id).map((p) => p.id);
  const sp = {};
  const mid = (x) => (x && typeof x === 'object' ? x.id : x);
  (marriages || []).forEach((m) => {
    const a = mid(m.person1_id ?? m.person1); const b = mid(m.person2_id ?? m.person2);
    if (a && b) { (sp[a] = sp[a] || []).push(b); (sp[b] = sp[b] || []).push(a); }
  });
  // Marriage link na ho to bhi: ek hi bache ka father + mother = miyan biwi
  people.forEach((p) => {
    const pf = p.father_id ?? p.father; const pm = p.mother_id ?? p.mother;
    if (pf && pm && by[pf] && by[pm]) {
      if (!(sp[pf] || []).includes(pm)) (sp[pf] = sp[pf] || []).push(pm);
      if (!(sp[pm] || []).includes(pf)) (sp[pm] = sp[pm] || []).push(pf);
    }
  });
  const spouses = (id) => sp[id] || [];
  const set = (id, key) => { if (id && id !== meId && by[id] && !R[id]) R[id] = key; };
  const older = (a, b) => dob(a) && dob(b) && dob(a) < dob(b);

  const F = fa(meId), M = mo(meId);
  set(F, 'rel_father'); set(M, 'rel_mother');
  // spouse
  spouses(meId).forEach((s1) => set(s1, g(s1) === 'm' ? 'rel_husband' : g(s1) === 'f' ? 'rel_wife' : 'rel_spouse'));
  // children + un ke jeevan-saathi + pote/nawase
  kids(meId).forEach((c) => {
    set(c, g(c) === 'm' ? 'rel_son' : g(c) === 'f' ? 'rel_daughter' : 'rel_child');
    spouses(c).forEach((cs) => set(cs, g(c) === 'm' ? 'rel_bahu' : g(c) === 'f' ? 'rel_damad' : 'rel_relative'));
    kids(c).forEach((gc) => {
      if (g(c) === 'm') set(gc, g(gc) === 'f' ? 'rel_poti' : g(gc) === 'm' ? 'rel_pota' : 'rel_grandchild');
      else if (g(c) === 'f') set(gc, g(gc) === 'f' ? 'rel_nawasi' : g(gc) === 'm' ? 'rel_nawasa' : 'rel_grandchild');
      else set(gc, 'rel_grandchild');
    });
  });
  // siblings + un ke rishtay
  const sibs = [];
  people.forEach((p) => {
    if (p.id === meId) return;
    const pf = fa(p.id), pm = mo(p.id);
    if ((F && pf === F) || (M && pm === M)) sibs.push(p.id);
  });
  sibs.forEach((sb) => {
    const gs = g(sb);
    set(sb, gs === 'm' ? 'rel_brother' : gs === 'f' ? 'rel_sister' : 'rel_sibling');
    spouses(sb).forEach((ss) => set(ss, gs === 'm' ? 'rel_bhabhi' : gs === 'f' ? 'rel_behnoi' : 'rel_relative'));
    kids(sb).forEach((nk) => {
      if (gs === 'm') set(nk, g(nk) === 'f' ? 'rel_bhatiji' : g(nk) === 'm' ? 'rel_bhatija' : 'rel_sib_child');
      else if (gs === 'f') set(nk, g(nk) === 'f' ? 'rel_bhanji' : g(nk) === 'm' ? 'rel_bhanja' : 'rel_sib_child');
      else set(nk, 'rel_sib_child');
    });
  });
  // dada-dadi / nana-nani
  if (F) { set(fa(F), 'rel_dada'); set(mo(F), 'rel_dadi'); }
  if (M) { set(fa(M), 'rel_nana'); set(mo(M), 'rel_nani'); }
  // chacha/tayya/phupho + mamu/khala + un ke saathi + cousins
  const parentSibs = (par, paternal) => {
    if (!par) return;
    const pf = fa(par), pm = mo(par);
    people.forEach((p) => {
      if (p.id === par) return;
      const isSib = (pf && fa(p.id) === pf) || (pm && mo(p.id) === pm);
      if (!isSib) return;
      const gp = g(p.id);
      let key;
      if (paternal) {
        if (gp === 'm') key = older(p.id, par) ? 'rel_tayya' : 'rel_chacha';
        else if (gp === 'f') key = 'rel_phupho';
        else key = 'rel_relative';
      } else {
        if (gp === 'm') key = 'rel_mamu';
        else if (gp === 'f') key = 'rel_khala';
        else key = 'rel_relative';
      }
      set(p.id, key);
      spouses(p.id).forEach((psp) => {
        let sk = 'rel_relative';
        if (key === 'rel_chacha') sk = 'rel_chachi';
        else if (key === 'rel_tayya') sk = 'rel_tayi';
        else if (key === 'rel_phupho') sk = 'rel_phupha';
        else if (key === 'rel_mamu') sk = 'rel_mumani';
        else if (key === 'rel_khala') sk = 'rel_khalu';
        set(psp, sk);
      });
      kids(p.id).forEach((cz) => set(cz, 'rel_cousin'));
    });
  };
  parentSibs(F, true);
  parentSibs(M, false);
  // SUSRAAL — spouse ki taraf
  spouses(meId).forEach((s1) => {
    const SF = fa(s1), SM = mo(s1);
    set(SF, 'rel_susar'); set(SM, 'rel_saas');
    people.forEach((p) => {
      if (p.id === s1 || p.id === meId) return;
      const isSib = (SF && fa(p.id) === SF) || (SM && mo(p.id) === SM);
      if (!isSib) return;
      const gp = g(p.id); const myG = g(meId);
      let key = 'rel_inlaw';
      if (myG === 'm') key = gp === 'm' ? 'rel_saala' : gp === 'f' ? 'rel_saali' : 'rel_inlaw';
      else if (myG === 'f') {
        if (gp === 'm') key = older(p.id, s1) ? 'rel_jeth' : 'rel_dewar';
        else if (gp === 'f') key = 'rel_nand';
      }
      set(p.id, key);
    });
  });
  // baqi jitne linked hain
  people.forEach((p) => { if (p.id !== meId && !R[p.id]) R[p.id] = 'rel_relative'; });
  R[meId] = 'rel_me';
  return R;
}

const FT_INLAW_KEYS = ['rel_susar', 'rel_saas', 'rel_saala', 'rel_saali', 'rel_dewar', 'rel_jeth', 'rel_nand', 'rel_inlaw'];

const REL_UR = {
  rel_me: 'خود', rel_father: 'والد', rel_mother: 'والدہ', rel_husband: 'شوہر', rel_wife: 'بیوی', rel_spouse: 'شریکِ حیات',
  rel_son: 'بیٹا', rel_daughter: 'بیٹی', rel_child: 'اولاد', rel_bahu: 'بہو', rel_damad: 'داماد',
  rel_pota: 'پوتا', rel_poti: 'پوتی', rel_nawasa: 'نواسا', rel_nawasi: 'نواسی', rel_grandchild: 'پوتا/نواسا',
  rel_brother: 'بھائی', rel_sister: 'بہن', rel_sibling: 'بہن/بھائی', rel_bhabhi: 'بھابھی', rel_behnoi: 'بہنوئی',
  rel_bhatija: 'بھتیجا', rel_bhatiji: 'بھتیجی', rel_bhanja: 'بھانجا', rel_bhanji: 'بھانجی', rel_sib_child: 'بھتیجا/بھانجا',
  rel_dada: 'دادا', rel_dadi: 'دادی', rel_nana: 'نانا', rel_nani: 'نانی',
  rel_chacha: 'چچا', rel_tayya: 'تایا', rel_chachi: 'چچی', rel_tayi: 'تائی',
  rel_phupho: 'پھوپھو', rel_phupha: 'پھوپھا', rel_mamu: 'ماموں', rel_mumani: 'ممانی',
  rel_khala: 'خالہ', rel_khalu: 'خالو', rel_cousin: 'کزن',
  rel_susar: 'سسر', rel_saas: 'ساس', rel_saala: 'سالا', rel_saali: 'سالی',
  rel_dewar: 'دیور', rel_jeth: 'جیٹھ', rel_nand: 'نند', rel_inlaw: 'سسرالی رشتہ دار', rel_relative: 'رشتہ دار',
};
const REL_EN = {
  rel_me: 'Me', rel_father: 'Father', rel_mother: 'Mother', rel_husband: 'Husband', rel_wife: 'Wife', rel_spouse: 'Spouse',
  rel_son: 'Son', rel_daughter: 'Daughter', rel_child: 'Child', rel_bahu: 'Daughter-in-law', rel_damad: 'Son-in-law',
  rel_pota: 'Grandson (son side)', rel_poti: 'Granddaughter (son side)',
  rel_nawasa: 'Grandson (daughter side)', rel_nawasi: 'Granddaughter (daughter side)', rel_grandchild: 'Grandchild',
  rel_brother: 'Brother', rel_sister: 'Sister', rel_sibling: 'Sibling',
  rel_bhabhi: 'Sister-in-law (brother wife)', rel_behnoi: 'Brother-in-law (sister husband)',
  rel_bhatija: 'Nephew (brother son)', rel_bhatiji: 'Niece (brother daughter)',
  rel_bhanja: 'Nephew (sister son)', rel_bhanji: 'Niece (sister daughter)', rel_sib_child: 'Nephew / Niece',
  rel_dada: 'Grandfather (paternal)', rel_dadi: 'Grandmother (paternal)',
  rel_nana: 'Grandfather (maternal)', rel_nani: 'Grandmother (maternal)',
  rel_chacha: 'Uncle (father side, younger)', rel_tayya: 'Uncle (father side, elder)',
  rel_chachi: 'Aunt (chacha wife)', rel_tayi: 'Aunt (taya wife)',
  rel_phupho: 'Aunt (father sister)', rel_phupha: 'Uncle (phupho husband)',
  rel_mamu: 'Uncle (mother brother)', rel_mumani: 'Aunt (mamu wife)',
  rel_khala: 'Aunt (mother sister)', rel_khalu: 'Uncle (khala husband)', rel_cousin: 'Cousin',
  rel_susar: 'Father-in-law', rel_saas: 'Mother-in-law',
  rel_saala: 'Brother-in-law (wife brother)', rel_saali: 'Sister-in-law (wife sister)',
  rel_dewar: 'Brother-in-law (husband younger)', rel_jeth: 'Brother-in-law (husband elder)',
  rel_nand: 'Sister-in-law (husband sister)', rel_inlaw: 'In-law relative', rel_relative: 'Relative',
};
const ftYear = (p) => {
  const d = p.date_of_birth || p.dob;
  return d ? String(d).slice(0, 4) : '';
};

// SHAJRA TARTEEB: walid/walida -> sab se bari aulad (+ jeevan saathi) -> uske bache -> phir agli aulad ki shakh
function ftShajraOrder(people, marriages) {
  const by = {}; (people || []).forEach((p) => { by[p.id] = p; });
  const fa = (p) => p.father_id ?? p.father ?? null;
  const mo = (p) => p.mother_id ?? p.mother ?? null;
  const mid = (x) => (x && typeof x === 'object' ? x.id : x);
  const sp = {};
  (marriages || []).forEach((m) => {
    const a = mid(m.person1_id ?? m.person1); const b = mid(m.person2_id ?? m.person2);
    if (a && b) { sp[a] = sp[a] || b; sp[b] = sp[b] || a; }
  });
  (people || []).forEach((p) => {
    const f = fa(p); const m2 = mo(p);
    if (f && m2 && by[f] && by[m2]) { sp[f] = sp[f] || m2; sp[m2] = sp[m2] || f; }
  });
  const kidsOf = {};
  (people || []).forEach((p) => {
    [fa(p), mo(p)].forEach((pid) => {
      if (pid && by[pid]) { kidsOf[pid] = kidsOf[pid] || []; if (!kidsOf[pid].includes(p.id)) kidsOf[pid].push(p.id); }
    });
  });
  const byAge = (a, b) => {
    const da = (by[a].date_of_birth || by[a].dob || '9999-99-99');
    const db = (by[b].date_of_birth || by[b].dob || '9999-99-99');
    return da < db ? -1 : da > db ? 1 : 0;
  };
  const hasParent = new Set();
  (people || []).forEach((p) => { if ((fa(p) && by[fa(p)]) || (mo(p) && by[mo(p)])) hasParent.add(p.id); });
  let roots = (people || []).filter((p) => !hasParent.has(p.id)).map((p) => p.id);
  roots = roots.filter((id) => !(sp[id] && hasParent.has(sp[id])));
  roots.sort(byAge);

  const out = []; const seen = new Set();
  const push = (id, depth) => { if (!by[id] || seen.has(id)) return; seen.add(id); out.push({ p: by[id], depth }); };
  const walk = (id, depth) => {
    if (seen.has(id)) return;
    push(id, depth);
    if (sp[id]) push(sp[id], depth);                       // jeevan saathi sath hi
    const kids = [...new Set([...(kidsOf[id] || []), ...(sp[id] ? (kidsOf[sp[id]] || []) : [])])].sort(byAge);
    kids.forEach((k) => walk(k, depth + 1));               // bari aulad pehle, uski poori shakh
  };
  roots.forEach((r) => walk(r, 0));
  (people || []).forEach((p) => push(p.id, 0));            // jo reh gaya
  return out;
}

function FamilyRelations({ tree, treeId, people, marriages }) {
  const tid = treeId || (tree && tree.id) || null;
  const ppl = ((people && people.length) ? people : (tree && (tree.people || tree.persons))) || [];
  const mrg = ((marriages && marriages.length) ? marriages : (tree && tree.marriages)) || [];
  const [meta, setMeta] = useState(null);
  const loadMeta = useCallback(() => {
    if (!tid) { setMeta(null); return; }
    fetch(`${FT_REL_API}/trees/${tid}/relmeta/`, { headers: ftAuthH() })
      .then((r) => r.json()).then(setMeta).catch(() => setMeta({}));
  }, [tid]);
  useEffect(() => { loadMeta(); }, [loadMeta]);
  if (!tid || !meta || ppl.length === 0) return null;
  const meId = meta.me_person_id || null;
  const genders = meta.genders || {};
  const R = computeRelations(ppl, mrg, meId, genders);
  const setMe = (id) => {
    fetch(`${FT_REL_API}/trees/${tid}/relmeta/`, { method: 'POST', headers: ftAuthH(), body: JSON.stringify({ me_person_id: id || null }) }).then(loadMeta);
  };
  const setG = (pid, gv) => {
    fetch(`${FT_REL_API}/trees/${tid}/relmeta/`, { method: 'POST', headers: ftAuthH(), body: JSON.stringify({ person_id: pid, gender: gv }) }).then(loadMeta);
  };
  const ordered = ftShajraOrder(ppl, mrg).filter((x) => R[x.p.id]);
  const noG = ppl.filter((p) => !genders[p.id]);
  const row = ({ p, depth }) => {
    const k = R[p.id] || 'rel_relative';
    const inLaw = FT_INLAW_KEYS.includes(k);
    return (
      <div key={p.id} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '6px 0', borderBottom: '0.5px solid #EDF2F7', paddingLeft: Math.min(depth, 5) * 14 }}>
        <span style={{ fontSize: 13 }}>
          {depth > 0 && <span style={{ color: '#CBD5E1' }}>└ </span>}
          {p.id === meId ? '⭐ ' : ''}{ftName(p)}
          {ftYear(p) && <span style={{ fontSize: 10.5, color: '#94A3B8', marginLeft: 5 }}>{ftYear(p)}</span>}
        </span>
        <span style={{ textAlign: 'right', flexShrink: 0, marginLeft: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: inLaw ? '#4f46e5' : '#0F6E56', display: 'block' }}>{inLaw ? '💍 ' : ''}{REL_UR[k] || REL_UR.rel_relative}</span>
          <span style={{ fontSize: 10, color: '#64748B', display: 'block', lineHeight: 1.3 }}>{REL_EN[k] || REL_EN.rel_relative}</span>
        </span>
      </div>
    );
  };
  return (
    <div style={{ background: '#fff', border: '0.5px solid #E2E8F0', borderRadius: 12, padding: '0.9rem', margin: '10px 0' }}>
      <p style={{ fontSize: 15, fontWeight: 800, margin: '0 0 8px' }}>{t('rel_panel')}</p>
      <label style={{ fontSize: 12.5, fontWeight: 600 }}>{t('rel_pick_me')}</label>
      <select style={{ width: '100%', padding: 8, fontSize: 13, border: '0.5px solid #E2E8F0', borderRadius: 8, margin: '4px 0 10px' }}
        value={meId || ''} onChange={(e) => setMe(e.target.value ? Number(e.target.value) : null)}>
        <option value="">--</option>
        {ppl.map((p) => <option key={p.id} value={p.id}>{ftName(p)}</option>)}
      </select>
      {meId && (
        <>
          <p style={{ fontSize: 12, color: '#64748B', margin: '6px 0 4px' }}>{t('rel_family')} ({ordered.length}) · 💍 {t('rel_inlaws')}</p>
          {ordered.map(row)}
          {noG.length > 0 && (<>
            <p style={{ fontSize: 12, fontWeight: 600, color: '#64748B', margin: '12px 0 4px' }}>{t('rel_set_gender')}</p>
            {noG.map((p) => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
                <span style={{ fontSize: 12.5, flex: 1 }}>{ftName(p)}</span>
                <button style={{ padding: '3px 10px', fontSize: 12, border: '0.5px solid #E2E8F0', borderRadius: 8, background: '#fff', cursor: 'pointer' }} onClick={() => setG(p.id, 'm')}>♂</button>
                <button style={{ padding: '3px 10px', fontSize: 12, border: '0.5px solid #E2E8F0', borderRadius: 8, background: '#fff', cursor: 'pointer' }} onClick={() => setG(p.id, 'f')}>♀</button>
              </div>
            ))}
          </>)}
        </>
      )}
    </div>
  );
}


// ================= 📖 Album (warqe ultne wala) + 📇 Directory + 📣 Events =================
function useRelMeta(treeId) {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    if (!treeId) return;
    fetch(`${FT_REL_API}/trees/${treeId}/relmeta/`, { headers: ftAuthH() })
      .then((r) => r.json()).then(setMeta).catch(() => setMeta({}));
  }, [treeId]);
  return meta || {};
}

function ftRelLabel(R, id) {
  const k = R[id];
  if (!k) return null;
  return { ur: REL_UR[k] || REL_UR.rel_relative, en: REL_EN[k] || REL_EN.rel_relative, inLaw: FT_INLAW_KEYS.includes(k) };
}

function ShajraAlbum({ tree }) {
  const meta = useRelMeta(tree && tree.id);
  const [i, setI] = useState(0);
  const [flip, setFlip] = useState(0);
  const touch = useRef(null);
  const ordered = ftShajraOrder(tree.people || [], tree.marriages || []);
  const pages = ordered.filter((x) => x.p.photo);
  const R = computeRelations(tree.people || [], tree.marriages || [], meta.me_person_id, meta.genders || {});
  if (pages.length === 0) return <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('ftx_no_photos')}</p>;
  const idx = Math.max(0, Math.min(i, pages.length - 1));
  const { p } = pages[idx];
  const lab = ftRelLabel(R, p.id);
  const go = (d) => { setFlip((f) => f + 1); setI((x) => Math.max(0, Math.min(pages.length - 1, x + d))); };
  return (
    <div style={{ textAlign: 'center' }}
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => { if (touch.current == null) return; const dx = e.changedTouches[0].clientX - touch.current; if (Math.abs(dx) > 45) go(dx < 0 ? 1 : -1); touch.current = null; }}>
      <style>{`@keyframes ftflip { from { transform: rotateY(-72deg); opacity: .25 } to { transform: rotateY(0deg); opacity: 1 } }`}</style>
      <div style={{ perspective: 1100 }}>
        <div key={flip} style={{ animation: 'ftflip 420ms ease', transformOrigin: 'left center', background: '#fff', border: `0.5px solid ${colors.border}`, borderRadius: 14, padding: '1rem', maxWidth: 320, margin: '0 auto', boxShadow: '0 6px 18px rgba(0,0,0,0.07)' }}>
          <img src={p.photo} alt={ftName(p)} style={{ width: '100%', maxWidth: 240, aspectRatio: '1/1', objectFit: 'cover', borderRadius: 10, margin: '0 auto 10px', display: 'block' }} />
          <p style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{ftName(p)}</p>
          <p style={{ fontSize: 12, color: colors.textMuted, margin: '2px 0 0' }}>
            {ftYear(p) || '—'}{p.birthplace ? ` · ${p.birthplace}` : ''}
          </p>
          {lab && <p style={{ fontSize: 13, fontWeight: 700, color: lab.inLaw ? '#4f46e5' : '#0F6E56', margin: '8px 0 0' }}>{lab.inLaw ? '💍 ' : ''}{lab.ur} <span style={{ fontSize: 10.5, color: '#64748B', fontWeight: 400 }}>{lab.en}</span></p>}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 12 }}>
        <button onClick={() => go(-1)} disabled={idx === 0} style={{ padding: '7px 14px', fontSize: 12, border: `0.5px solid ${colors.border}`, borderRadius: 8, background: '#fff', cursor: idx === 0 ? 'default' : 'pointer', opacity: idx === 0 ? 0.4 : 1 }}>{t('ftx_prev')}</button>
        <span style={{ fontSize: 12, color: colors.textMuted }}>{t('ftx_page')} {idx + 1}/{pages.length}</span>
        <button onClick={() => go(1)} disabled={idx === pages.length - 1} style={{ padding: '7px 14px', fontSize: 12, border: `0.5px solid ${colors.border}`, borderRadius: 8, background: '#fff', cursor: idx === pages.length - 1 ? 'default' : 'pointer', opacity: idx === pages.length - 1 ? 0.4 : 1 }}>{t('ftx_next')}</button>
      </div>
    </div>
  );
}

function FamilyDirectory({ tree }) {
  const meta = useRelMeta(tree && tree.id);
  const ordered = ftShajraOrder(tree.people || [], tree.marriages || []);
  const R = computeRelations(tree.people || [], tree.marriages || [], meta.me_person_id, meta.genders || {});
  return (
    <div>
      <p style={{ fontSize: 11.5, color: colors.textMuted, margin: '0 0 8px' }}>{t('ftx_dir_hint')}</p>
      {ordered.map(({ p, depth }) => {
        const lab = ftRelLabel(R, p.id);
        const ph = (p.phone || '').replace(/[^0-9+]/g, '');
        return (
          <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 0', borderBottom: `0.5px solid ${colors.border}`, paddingLeft: Math.min(depth, 5) * 12 }}>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>{depth > 0 && <span style={{ color: '#CBD5E1' }}>└ </span>}{ftName(p)}</p>
              <p style={{ fontSize: 11, color: colors.textMuted, margin: 0 }}>
                {lab ? `${lab.inLaw ? '💍 ' : ''}${lab.ur} · ${lab.en}` : (ftYear(p) || '')}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              {ph ? (<>
                <a href={`tel:${ph}`} style={{ fontSize: 11, padding: '5px 9px', border: `0.5px solid ${colors.border}`, borderRadius: 8, textDecoration: 'none', color: colors.text }}>{t('ftx_call')}</a>
                <a href={`https://wa.me/${ph.replace(/^\+/, '')}`} target="_blank" rel="noreferrer" style={{ fontSize: 11, padding: '5px 9px', border: `0.5px solid ${colors.border}`, borderRadius: 8, textDecoration: 'none', color: colors.text }}>{t('ftx_wa')}</a>
              </>) : <span style={{ fontSize: 10.5, color: '#94A3B8' }}>{t('ftx_no_phone')}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FamilyEventsPanel({ tree }) {
  const [evs, setEvs] = useState(null);
  const [anns, setAnns] = useState(null);
  const [isMember, setIsMember] = useState(false);
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ title: '', event_type: 'gathering', event_date: '', location: '', description: '' });
  const [msg, setMsg] = useState('');
  const tid = tree && tree.id;
  const load = useCallback(() => {
    fetch(`${FT_REL_API}/trees/${tid}/events/`, { headers: ftAuthH() }).then((r) => r.json())
      .then((d) => { setEvs(d.events || []); setIsMember(!!d.is_member); }).catch(() => setEvs([]));
    fetch(`${FT_REL_API}/trees/${tid}/announcements/`, { headers: ftAuthH() }).then((r) => r.json())
      .then((d) => setAnns(d.announcements || [])).catch(() => setAnns([]));
  }, [tid]);
  useEffect(() => { if (tid) load(); }, [tid, load]);

  const saveEvent = async () => {
    if (!f.title.trim()) return;
    const r = await fetch(`${FT_REL_API}/trees/${tid}/events/`, { method: 'POST', headers: ftAuthH(), body: JSON.stringify(f) });
    const d = await safeJson(r);
    if (!r.ok) { alert(d.error || 'failed'); return; }
    setF({ title: '', event_type: 'gathering', event_date: '', location: '', description: '' }); setShow(false); load();
  };
  const postAnn = async () => {
    if (!msg.trim()) return;
    const r = await fetch(`${FT_REL_API}/trees/${tid}/announcements/`, { method: 'POST', headers: ftAuthH(), body: JSON.stringify({ message: msg }) });
    const d = await safeJson(r);
    if (!r.ok) { alert(d.error || 'failed'); return; }
    setMsg(''); load();
  };
  const delEv = async (id) => { await fetch(`${FT_REL_API}/events/${id}/delete/`, { method: 'DELETE', headers: ftAuthH() }); load(); };
  const delAnn = async (id) => { await fetch(`${FT_REL_API}/announcements/${id}/delete/`, { method: 'DELETE', headers: ftAuthH() }); load(); };

  const EMO = { gathering: '🎪', wedding: '💍', birth: '👶', death: '🕊️', birthday: '🎂', other: '📌' };
  const inp = { width: '100%', padding: '8px 10px', fontSize: 13, border: `0.5px solid ${colors.border}`, borderRadius: 8, boxSizing: 'border-box', marginBottom: 6 };

  return (
    <div>
      {/* Announcements */}
      <p style={{ fontSize: 14, fontWeight: 700, margin: '0 0 6px' }}>{t('ftx_ann')}</p>
      {isMember && (
        <div style={{ marginBottom: 10 }}>
          <textarea style={{ ...inp, minHeight: 56 }} placeholder={t('ftx_ann_ph')} value={msg} onChange={(e) => setMsg(e.target.value)} />
          <button onClick={postAnn} style={{ padding: '7px 14px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('ftx_ann_post')}</button>
        </div>
      )}
      {!anns ? null : anns.length === 0 ? <p style={{ fontSize: 12, color: colors.textMuted }}>{t('ftx_no_ann')}</p> : anns.map((a) => (
        <div key={a.id} style={{ background: a.is_pinned ? '#ECFDF5' : '#fff', border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: '8px 10px', marginBottom: 6 }}>
          <p style={{ fontSize: 13, margin: 0, whiteSpace: 'pre-wrap' }}>{a.is_pinned ? '📌 ' : ''}{a.message}</p>
          <p style={{ fontSize: 10.5, color: colors.textMuted, margin: '4px 0 0' }}>
            @{a.by} · {a.date}
            {a.can_delete && <button onClick={() => delAnn(a.id)} style={{ marginLeft: 8, fontSize: 10.5, border: 'none', background: 'none', color: '#C0392B', cursor: 'pointer' }}>{t('ftx_del')}</button>}
          </p>
        </div>
      ))}

      {/* Events */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '16px 0 6px' }}>
        <p style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>{t('ftx_events')}</p>
        {isMember && <button onClick={() => setShow(!show)} style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('ftx_add_event')}</button>}
      </div>
      {!isMember && <p style={{ fontSize: 11.5, color: colors.textMuted, margin: '0 0 8px' }}>{t('ftx_members_only')}</p>}
      {show && (
        <div style={{ background: '#F8FAF9', borderRadius: 10, padding: 10, marginBottom: 10 }}>
          <input style={inp} placeholder={t('ftx_ev_title')} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          <select style={inp} value={f.event_type} onChange={(e) => setF({ ...f, event_type: e.target.value })}>
            <option value="gathering">🎪 {t('ftx_t_gathering')}</option>
            <option value="wedding">💍 {t('ftx_t_wedding')}</option>
            <option value="birthday">🎂 {t('ftx_t_birthday')}</option>
            <option value="birth">👶 {t('ftx_t_birth')}</option>
            <option value="death">🕊️ {t('ftx_t_death')}</option>
            <option value="other">📌 {t('ftx_t_other')}</option>
          </select>
          <input style={inp} type="date" value={f.event_date} onChange={(e) => setF({ ...f, event_date: e.target.value })} />
          <input style={inp} placeholder={t('ftx_ev_place')} value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} />
          <textarea style={{ ...inp, minHeight: 50 }} placeholder={t('ftx_ev_note')} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          <button onClick={saveEvent} style={{ padding: '7px 14px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('ftx_ev_save')}</button>
        </div>
      )}
      {!evs ? null : evs.length === 0 ? <p style={{ fontSize: 12, color: colors.textMuted }}>{t('ftx_no_events')}</p> : evs.map((e) => (
        <div key={e.id} style={{ background: '#fff', border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: '8px 10px', marginBottom: 6 }}>
          <p style={{ fontSize: 13.5, fontWeight: 700, margin: 0 }}>{EMO[e.event_type] || '📌'} {e.title}</p>
          <p style={{ fontSize: 11.5, color: colors.textMuted, margin: '2px 0 0' }}>
            {e.event_date || ''}{e.location ? ` · 📍 ${e.location}` : ''} · @{e.by}
            {e.can_delete && <button onClick={() => delEv(e.id)} style={{ marginLeft: 8, fontSize: 10.5, border: 'none', background: 'none', color: '#C0392B', cursor: 'pointer' }}>{t('ftx_del')}</button>}
          </p>
          {e.description && <p style={{ fontSize: 12, margin: '6px 0 0', whiteSpace: 'pre-wrap' }}>{e.description}</p>}
        </div>
      ))}
    </div>
  );
}